import {
  DATE_FORMAT_DMY,
  dateISOApplyTimezone,
  formatDatetime,
  formatHHmmTime,
  formatTimeISO,
  RideStatusEnum,
  SelectionEnum,
  timezoneAdjustedDateToISO,
  TimeZoneEnum,
  type DateYMDString,
  type IRideDto,
  type IRidesCalendarEventDto,
  type TTimeHHmm,
} from "@ryde/shared";

import {
  RIDES_CALENDAR_DATE_PART_PAD_LENGTH,
  RIDES_CALENDAR_HOUR_LABEL_PAD_LENGTH,
  RIDES_CALENDAR_HOUR_ROW_COUNT,
  RIDES_CALENDAR_STATUS_COLOR,
  RIDES_CALENDAR_STATUS_FALLBACK_COLOR,
} from "./parts/rides-calendar.constants";
import type {
  IRideEventPlacement,
  RidesCalendarRowsFilter,
} from "./parts/rides-calendar.models";

export const getRideStatusColor = (status: RideStatusEnum): string =>
  RIDES_CALENDAR_STATUS_COLOR[status] ?? RIDES_CALENDAR_STATUS_FALLBACK_COLOR;

export const getRideEventPlacement = (
  event: IRidesCalendarEventDto,
  timezone: TimeZoneEnum,
): IRideEventPlacement | undefined => {
  const local = dateISOApplyTimezone(event.start, timezone);
  if (!local.isValid()) {
    return undefined;
  }
  const hour = local.hour();
  if (hour < 0 || hour >= RIDES_CALENDAR_HOUR_ROW_COUNT) {
    return undefined;
  }
  return {
    dateKey: local.format("YYYY-MM-DD") as DateYMDString,
    hour,
  };
};

export const cellKey = (dateKey: DateYMDString, hour: number): string =>
  `${dateKey}|${hour}`;

export const buildRidesCalendarEventMap = ({
  events,
  dateKeys,
  timezone,
}: {
  events: IRidesCalendarEventDto[];
  dateKeys: DateYMDString[];
  timezone: TimeZoneEnum;
}): Map<string, IRidesCalendarEventDto[]> => {
  const allowedDateKeys = new Set(dateKeys);
  const map = new Map<string, IRidesCalendarEventDto[]>();

  for (const event of events) {
    const placement = getRideEventPlacement(event, timezone);
    if (!placement || !allowedDateKeys.has(placement.dateKey)) {
      continue;
    }
    const key = cellKey(placement.dateKey, placement.hour);
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(event);
    } else {
      map.set(key, [event]);
    }
  }
  for (const bucket of map.values()) {
    bucket.sort((a, b) => a.start.localeCompare(b.start));
  }
  return map;
};

export const formatRideEventTime = ({
  iso,
  timeFormat,
  timezone,
}: {
  iso: TDateISO;
  timeFormat: string;
  timezone: TimeZoneEnum;
}): string => formatTimeISO({ dateTimeISO: iso, timeFormat, timezone });

export const formatRideEventDateTime = ({
  iso,
  timeFormat,
  timezone,
}: {
  iso: TDateISO;
  timeFormat: string;
  timezone: TimeZoneEnum;
}): string =>
  formatDatetime({
    dateTimeISO: iso,
    dateFormat: DATE_FORMAT_DMY,
    timeFormat,
    timezone,
  });

/** Row labels are wall-clock hours, so they are formatted without a timezone shift. */
export const formatRideCalendarHourLabel = (
  hourIndex: number,
  timeFormat: string,
): string =>
  formatHHmmTime(
    `${String(hourIndex).padStart(RIDES_CALENDAR_HOUR_LABEL_PAD_LENGTH, "0")}:00` as TTimeHHmm,
    timeFormat,
    TimeZoneEnum.UTC,
  );

export const slotStartIso = ({
  dateKey,
  hour,
  timezone,
}: {
  dateKey: DateYMDString;
  hour: number;
  timezone: TimeZoneEnum;
}): TDateISO =>
  timezoneAdjustedDateToISO(
    `${dateKey} ${String(hour).padStart(RIDES_CALENDAR_DATE_PART_PAD_LENGTH, "0")}:00`,
    timezone,
  );

const collectIdsFromMap = (
  map: Map<string, IRidesCalendarEventDto[]>,
  keyMatcher: (key: string) => boolean,
): number[] => {
  const ids: number[] = [];
  for (const [key, bucket] of map.entries()) {
    if (!keyMatcher(key)) {
      continue;
    }
    for (const event of bucket) {
      ids.push(event.id);
    }
  }
  return ids;
};

export const getRideIdsForColumn = (
  eventMap: Map<string, IRidesCalendarEventDto[]>,
  dateKey: DateYMDString,
): number[] =>
  collectIdsFromMap(eventMap, key => key.startsWith(`${dateKey}|`));

export const getRideIdsForRow = (
  eventMap: Map<string, IRidesCalendarEventDto[]>,
  hourIndex: number,
): number[] =>
  collectIdsFromMap(eventMap, key => key.endsWith(`|${String(hourIndex)}`));

export const getAllRideIds = (
  eventMap: Map<string, IRidesCalendarEventDto[]>,
): number[] => collectIdsFromMap(eventMap, () => true);

export const computeSelectionState = (
  ids: number[],
  selected: ReadonlySet<number>,
): SelectionEnum => {
  if (!ids.length) {
    return SelectionEnum.None;
  }
  let selectedCount = 0;
  for (const id of ids) {
    if (selected.has(id)) {
      selectedCount += 1;
    }
  }
  if (selectedCount === 0) {
    return SelectionEnum.None;
  }
  if (selectedCount === ids.length) {
    return SelectionEnum.All;
  }
  return SelectionEnum.Some;
};

export const applySelectionToIds = ({
  ids,
  selectedIds,
  value,
}: {
  ids: number[];
  selectedIds: number[];
  value: SelectionEnum;
}): number[] => {
  if (!ids.length) {
    return selectedIds;
  }
  if (value === SelectionEnum.All) {
    return Array.from(new Set([...selectedIds, ...ids]));
  }
  if (value === SelectionEnum.None) {
    const idSet = new Set(ids);
    return selectedIds.filter(id => !idSet.has(id));
  }
  return selectedIds;
};

const buildRideTitleFromDto = (ride: IRideDto): string => {
  const metadataTitle = ride.metadata?.title?.trim();
  if (metadataTitle) {
    return metadataTitle;
  }
  const origin = ride.originStationName;
  const destination = ride.destinationStationName;
  if (origin && destination) {
    return `${origin} → ${destination}`;
  }
  if (origin) {
    return origin;
  }
  if (destination) {
    return destination;
  }
  return `Ride #${ride.id}`;
};

export const mapRideToCalendarEvent = (
  ride: IRideDto,
): IRidesCalendarEventDto => ({
  id: ride.id,
  title: buildRideTitleFromDto(ride),
  start: ride.rideStart,
  end: ride.rideEnd ?? ride.rideStart,
  status: ride.status ?? RideStatusEnum.New,
  direction: ride.direction,
  siteId: ride.siteId ?? null,
  siteName: ride.siteName,
  supplierId: ride.supplierId ?? null,
  supplierName: ride.supplierName,
  driverId: ride.driverId ?? null,
  driverName: ride.driverName ?? ride.metadata?.driverInfo?.name,
  vehicleId: ride.vehicleId ?? null,
  vehicleName: ride.vehicleName,
  originStationName: ride.originStationName,
  destinationStationName: ride.destinationStationName,
  passengersCount: ride.metadata?.passengersCount,
  seatsCount: ride.metadata?.seatsCount,
  passengersInfo: ride.metadata?.passengersInfo,
  occupancy: ride.occupancy,
  maxPassengers: ride.metadata?.maxPassengers,
  courseId: ride.courseId ?? null,
  courseName: ride.courseName,
  courseRouteType: ride.courseRouteType,
});

export const getRidesCalendarVisibleHourIndices = ({
  eventMap,
  filter,
  hourRowCount,
}: {
  eventMap: Map<string, IRidesCalendarEventDto[]>;
  filter: RidesCalendarRowsFilter;
  hourRowCount: number;
}): number[] => {
  if (filter === "all") {
    return Array.from({ length: hourRowCount }, (_, i) => i);
  }

  const hoursWithRides = new Set<number>();
  for (const key of eventMap.keys()) {
    const hour = Number(key.split("|")[1]);
    if (!Number.isNaN(hour)) {
      hoursWithRides.add(hour);
    }
  }
  return Array.from(hoursWithRides).sort((a, b) => a - b);
};
